import * as Yup from "yup";
import { 
  OwnershipType as TrailerOwnershipType,
  ownerInfoValidationSchema,
  leaseFromInfoValidationSchema,
  leaseToInfoValidationSchema
} from "../../../types/ownership";

// Define interfaces locally to avoid import issues
export interface OwnerInfoFormValues {
  company: string;
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  ssnFein: string;
  bankAccount: string;
  contacts: string;
  email: string;
  website: string;
}

export interface LeaseFromInfoFormValues {
  lessorId: number | null;
  lessorName: string | null;
  startDate: string | null;
  endDate: string | null;
}

export interface LeaseToInfoFormValues {
  lesseeId: number | null;
  lesseeName: string | null;
  startDate: string | null;
  endDate: string | null;
}

// Re-export for trailer-specific usage
export { TrailerOwnershipType };

export const trailerValidationSchema = Yup.object({
  // General Section
  unitNumber: Yup.string()
    .max(50, "Unit number cannot exceed 50 characters"),
  isActive: Yup.boolean(),
  startDate: Yup.date().nullable(),
  endDate: Yup.date()
    .nullable()
    .test('is-after-start', 'End date must be after start date', function(value) {
      const { startDate } = this.parent;
      if (!startDate || !value) return true; // Skip validation if either date is null
      return new Date(value) > new Date(startDate);
    }),
  size: Yup.string()
    .max(50, "Size cannot exceed 50 characters"),
  isTanker: Yup.boolean(),

  // Lookup fields - either use existing ID or create new from name
  trailerTypeId: Yup.number()
    .positive("Trailer type ID must be greater than 0")
    .nullable(),
  trailerTypeName: Yup.string()
    .max(100, "Trailer type name cannot be longer than 100 characters")
    .nullable(),
  trailerStatusId: Yup.number()
    .positive("Trailer status ID must be greater than 0")
    .nullable(),
  trailerStatusName: Yup.string()
    .max(100, "Trailer status name cannot be longer than 100 characters")
    .nullable(),
  divisionId: Yup.number()
    .positive("Division ID must be greater than 0")
    .nullable(),
  divisionName: Yup.string()
    .max(100, "Division name cannot be longer than 100 characters")
    .nullable(),

  // Equipment Info Section
  color: Yup.string()
    .max(50, "Color cannot exceed 50 characters"),
  year: Yup.string()
    .max(4, "Year cannot exceed 4 characters"),
  vin: Yup.string()
    .max(50, "VIN cannot exceed 50 characters"),
  platesNumber: Yup.string()
    .max(50, "Plates number cannot exceed 50 characters"),
  makeId: Yup.number()
    .positive("Make ID must be greater than 0")
    .nullable(),
  makeName: Yup.string()
    .max(100, "Make name cannot be longer than 100 characters")
    .nullable(),
  modelId: Yup.number()
    .positive("Model ID must be greater than 0")
    .nullable(),
  modelName: Yup.string()
    .max(100, "Model name cannot be longer than 100 characters")
    .nullable(),

  // Reefer Section
  reeferMake: Yup.string()
    .max(100, "Reefer make cannot exceed 100 characters"),
  reeferModel: Yup.string()
    .max(100, "Reefer model cannot exceed 100 characters"),
  reeferVin: Yup.string()
    .max(50, "Reefer VIN cannot exceed 50 characters"),
  purchaseDate: Yup.date().nullable(),
  purchaseAmount: Yup.number()
    .min(0, "Purchase amount must be greater than or equal to 0")
    .nullable(),

  // Ownership Section - NEW STRUCTURE (using shared validation schemas)
  ownershipType: Yup.number().required("Ownership type is required"),
  ownerInfo: ownerInfoValidationSchema.when('ownershipType', {
    is: TrailerOwnershipType.NotCompanyOwned,
    then: (schema) => schema.required("Owner information is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  leaseFromInfo: leaseFromInfoValidationSchema.when('ownershipType', {
    is: TrailerOwnershipType.CompanyOwnedLeasedFrom,
    then: (schema) => schema.required("Lease from information is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  leaseToInfo: leaseToInfoValidationSchema.when('ownershipType', {
    is: TrailerOwnershipType.CompanyOwnedLeasedTo,
    then: (schema) => schema.required("Lease to information is required"),
    otherwise: (schema) => schema.nullable(),
  }),

  // Legacy ownership fields for backward compatibility
  isCompanyOwned: Yup.boolean(),
  isLeased: Yup.boolean(),

  // Safety Section
  dotInspectionExpiration: Yup.date().nullable(),
  platesExpirationDate: Yup.date().nullable(),
  insuranceExpirationDate: Yup.date().nullable(),
  insuranceValue: Yup.number()
    .min(0, "Insurance value must be greater than or equal to 0")
    .nullable(),
  hasCustomWarning: Yup.boolean(),

  // Dispatch Section
  category: Yup.string()
    .max(100, "Category cannot exceed 100 characters"),

  // Comments Section
  comments: Yup.string()
    .max(1000, "Comments cannot be longer than 1000 characters"),
});

export interface TrailerFormValues {
  // General Section
  unitNumber: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  size: string;
  isTanker: boolean;

  // Lookup fields
  trailerTypeId: number | null;
  trailerTypeName: string | null;
  trailerStatusId: number | null;
  trailerStatusName: string | null;
  divisionId: number | null;
  divisionName: string | null;

  // Equipment Info Section
  color: string;
  year: string;
  vin: string;
  platesNumber: string;
  makeId: number | null;
  makeName: string | null;
  modelId: number | null;
  modelName: string | null;

  // Reefer Section
  reeferMake: string;
  reeferModel: string;
  reeferVin: string;
  purchaseDate: string | null;
  purchaseAmount: number | null;

  // Ownership Section - NEW STRUCTURE  
  ownershipType: TrailerOwnershipType;
  ownerInfo: OwnerInfoFormValues | null;
  leaseFromInfo: LeaseFromInfoFormValues | null;
  leaseToInfo: LeaseToInfoFormValues | null;

  // Legacy ownership fields for backward compatibility
  isCompanyOwned: boolean;
  isLeased: boolean;

  // Safety Section
  dotInspectionExpiration: string | null;
  platesExpirationDate: string | null;
  insuranceExpirationDate: string | null;
  insuranceValue: number | null;
  hasCustomWarning: boolean;

  // Dispatch Section
  category: string;

  // Comments Section
  comments: string;
} 